import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { ChangelogInDb } from '@/api';
import { fetchChangelogs } from '@/services/changelogService';

const LAST_SEEN_KEY = 'gaia:changelog-last-seen';

/**
 * Owns the changelog modal's open state and the boot-time "what's new" check.
 * A store (rather than provide/inject) because both the sidebar and the
 * authenticated bootstrap composable need to trigger the modal, and the boot
 * path hands prefetched entries over to it.
 */
export const useChangelogStore = defineStore('changelog', () => {
  const isOpen = ref(false);
  const prefetchedChangelogs = ref<ChangelogInDb[] | null>(null);

  function open(prefetched?: ChangelogInDb[]): void {
    prefetchedChangelogs.value = prefetched ?? null;
    isOpen.value = true;
  }

  function close(): void {
    isOpen.value = false;
    prefetchedChangelogs.value = null;
  }

  /**
   * Record the newest entry the user has seen. The newest entry's server
   * timestamp is preferred over client time to avoid clock-skew silently
   * swallowing entries.
   */
  function markSeen(newestCreatedAt?: string): void {
    try {
      localStorage.setItem(LAST_SEEN_KEY, newestCreatedAt ?? new Date().toISOString());
    } catch {
      // Storage unavailable (private mode) — nothing to do.
    }
  }

  /**
   * Boot check: auto-open the modal when entries were created since the user
   * last saw the changelog. First-ever boot initializes the timestamp
   * silently. Failures are swallowed — this must never break bootstrap.
   */
  async function checkForNewChangelogs(): Promise<void> {
    try {
      const lastSeen = localStorage.getItem(LAST_SEEN_KEY);
      if (!lastSeen) {
        markSeen();
        return;
      }
      const result = await fetchChangelogs({ since: lastSeen, limit: 20 });
      if (!result || result.results.length === 0) {
        return;
      }
      open(result.results);
    } catch {
      // Silent — the changelog is never worth blocking or breaking boot for.
    }
  }

  return {
    isOpen,
    prefetchedChangelogs,
    open,
    close,
    markSeen,
    checkForNewChangelogs,
  };
});
